import type { IconButtonProps } from '@chakra-ui/react'
import type { MutableRefObject } from 'react'
import { forwardRef, useState } from 'react'

import { toaster } from '@app/components/ui/toaster'
import type { MarkdownEditorRef } from '@app/next/forms/markdownEditor/markdownEditor'
import AiFieldUpdateButton from '@app/pages/metrics/components/aiFieldUpdateButton'
import { useStore } from '@app/store'
import { MetricDescriptionSuggest } from '@graphql/documents/ai_assistant.graphql'
import type { MetricDescriptionSuggestMutation, MetricDescriptionSuggestMutationVariables } from '@graphql/queries'

type Props = Partial<IconButtonProps> & {
  metricId: string
  onSuccess?: (result: string) => void
}

const AiPopulateMetricDescriptionButton: ReturnType<typeof forwardRef<MarkdownEditorRef, Props>> = forwardRef(
  ({ metricId, onSuccess = () => {}, ...props }, ref) => {
    const [loading, setLoading] = useState(false)
    const actionMutation = useStore.use.actionMutation()

    const onClick = async () => {
      setLoading(true)

      try {
        const { data, error } = await actionMutation<
          MetricDescriptionSuggestMutation,
          MetricDescriptionSuggestMutationVariables
        >(MetricDescriptionSuggest, {
          metricId
        })

        if (error?.graphQLErrors?.length) {
          toaster.create({
            title: 'Error',
            description: error.graphQLErrors[0].message,
            type: 'error'
          })

          return
        }

        // force this cast, as we know it's always an object (i.e. not a function)
        const forwardedRef = ref as MutableRefObject<MarkdownEditorRef>

        const result = data?.metricDescriptionSuggest?.result
        const { setValue } = forwardedRef?.current || {}

        if (setValue && result) {
          setValue(result)
        }

        onSuccess(result)
      } finally {
        setLoading(false)
      }
    }

    return <AiFieldUpdateButton loading={loading} onClick={onClick} data-cy="metric-description-suggest" {...props} />
  }
)

export default AiPopulateMetricDescriptionButton
